import styled from "styled-components";

/**
 * MED-682 — the collapsible Edit History block inside the product detail modal,
 * sitting last in the card below the Delete and Edit actions.
 *
 * The panel still caps its own height and scrolls internally. Below the actions
 * it can no longer push them out of reach, but an uncapped list would make the
 * modal itself grow without limit, which the ticket rules out separately.
 */
export const EditHistoryWrapper = styled.div`
  width: 100%;
  margin-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.border.light};

  /* The controls row already carries 2rem of padding-bottom on mobile, so the
     full margin on top of it would open a 4rem gap between the buttons and
     this. */
  @media screen and (max-width: ${({ theme }) => theme.screen.mobile}) {
    margin-top: 0.5rem;
  }

  .toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 0.25rem;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    &:hover .toggle-label {
      color: ${({ theme }) => theme.text.progress};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.text.progress};
      outline-offset: 2px;
      border-radius: 0.25rem;
    }
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text.primary};
  }

  .toggle-count {
    min-width: 1.35rem;
    padding: 0.1rem 0.4rem;
    border-radius: 1rem;
    background-color: ${({ theme }) => theme.bg.prim_400};
    color: ${({ theme }) => theme.text.prim_100};
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
  }

  .chevron {
    flex-shrink: 0;
    font-size: 1.4rem;
    color: ${({ theme }) => theme.text.prim_100};
    transition: transform 0.2s ease;
  }

  .chevron.open {
    transform: rotate(180deg);
  }

  .panel {
    /* Capped and scrolled here rather than on the list, so the "show earlier"
       control scrolls with the entries instead of pinning the panel taller. */
    max-height: 20rem;
    overflow-y: auto;
    padding: 0.25rem 0.75rem 1rem;
    background-color: ${({ theme }) => theme.bg.tert_500};
    border-radius: 0.25rem;
  }

  .panel-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 1.25rem 0.25rem;
  }

  .state-text {
    font-size: 0.85rem;
    font-weight: 500;
    color: ${({ theme }) => theme.text.prim_100};
  }

  .state-note {
    font-size: 0.8rem;
    font-weight: 400;
    line-height: 1.4;
    color: ${({ theme }) => theme.text.prim_200};
  }

  .retry,
  .show-more {
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text.progress};
    text-decoration: underline;
  }

  .show-more {
    margin-top: 0.75rem;
    padding-left: 0.25rem;
  }

  .entries {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`;

/** One edit in the list. */
export const EditEntryItem = styled.li`
  padding: 0.85rem 0.25rem;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.border.light};
  }

  .entry-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .entry-date {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text.primary};
  }

  .entry-actor {
    font-size: 0.8rem;
    font-weight: 400;
    color: ${({ theme }) => theme.text.prim_100};
  }

  .actor-name {
    font-weight: 600;
  }

  /* An edit whose acting user was never recorded. Set apart so it does not read
     as somebody's name. */
  .actor-unattributed {
    font-style: italic;
    color: ${({ theme }) => theme.text.prim_200};
  }

  .entry-changes {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .change-row {
    display: grid;
    grid-template-columns: minmax(6rem, 0.9fr) 1fr auto 1fr;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.8rem;

    @media screen and (max-width: ${({ theme }) => theme.screen.mobile}) {
      grid-template-columns: 1fr auto 1fr;

      .change-field {
        grid-column: 1 / -1;
      }
    }
  }

  .change-field {
    font-weight: 500;
    color: ${({ theme }) => theme.text.prim_100};
  }

  /* The values are recorded verbatim and can be long (a packaging hierarchy
     reads "carton x10 > unit"), so they wrap rather than overflow the row. */
  .change-from,
  .change-to {
    overflow-wrap: anywhere;
  }

  .change-from {
    color: ${({ theme }) => theme.text.prim_200};
    text-decoration: line-through;
  }

  .change-arrow {
    color: ${({ theme }) => theme.text.prim_200};
  }

  .change-to {
    font-weight: 600;
    color: ${({ theme }) => theme.text.primary};
  }

  .entry-no-changes {
    font-size: 0.8rem;
    font-style: italic;
    color: ${({ theme }) => theme.text.prim_200};
  }
`;
